import { useCallback, useEffect, useState } from 'react';
import { Mail, Settings, Users } from 'lucide-react';

import { getInbox, getThread } from '../../services/mailIntelApi';
import MailThreadPanel, { labelTone, roleLabel } from './MailThreadPanel';
import DecisionMapPanel from './DecisionMapPanel';
import MailSettingsPanel from './MailSettingsPanel';
import {
  Chip,
  DrawerDetail,
  EmptyState,
  ErrorBanner,
  SectionLabel,
  Spinner,
  T,
  useDetailNav,
} from './shared';

const SECTIONS = [
  { key: 'decision', title: 'Needs your decision' },
  { key: 'attention', title: 'Needs your attention' },
  { key: 'other', title: 'Other' },
];

const ThreadRow = ({ row, selected, onClick }) => {
  const tone = labelTone(row.label);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3.5 rounded-2xl p-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40 ${
        selected
          ? 'border border-indigo-500/60 bg-indigo-950/20 shadow-md shadow-indigo-950/30 ring-1 ring-indigo-500/50'
          : 'border border-zinc-800/80 bg-zinc-900/80 shadow-sm hover:border-zinc-700 hover:bg-zinc-800/60'
      }`}
    >
      <Chip fg={tone.fg} bg={tone.bg}>
        {row.label}
      </Chip>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-zinc-100">
          {row.subject || 'Untitled thread'}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-zinc-400">
          {(row.reasons || []).join(' · ') || 'No open signals'}
        </p>
      </div>
      {row.overdue && (
        // The nearest due date on the thread: an open item, or the linked
        // deliverable. It is not about how long the mail has waited.
        <span title="A due date on this thread has passed (an open item or the linked deliverable)">
          <Chip fg={T.danger} bg={T.dangerBg}>
            Due date passed
          </Chip>
        </span>
      )}
      {row.open_items > 0 && <Chip>{row.open_items} open</Chip>}
    </button>
  );
};

/**
 * Mail tab: the project's tracked threads ranked for the viewer's delivery
 * role. The list and panel come from stored data (no model); the panel's
 * summary loads on its own after the panel opens.
 */
const MailTriageList = ({ accessToken }) => {
  const [inbox, setInbox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {
    selectedId,
    detail,
    loading: detailLoading,
    error: detailError,
    open: openDetail,
    close: closeDetail,
  } = useDetailNav(async (threadId) => {
    const res = await getThread(accessToken, threadId);
    return res?.data;
  });

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getInbox(accessToken);
      setInbox(res?.data || null);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  // First load only: a later reload (after a map save) keeps the page and
  // its open drawer instead of swapping both for a spinner.
  if (loading && !inbox) return <Spinner />;

  const total = SECTIONS.reduce((n, s) => n + (inbox?.[s.key]?.length || 0), 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ErrorBanner>{error}</ErrorBanner>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] text-zinc-400">
          {inbox?.role && (
            <>
              Ranked for your role:{' '}
              <span className="font-semibold text-zinc-200">
                {roleLabel(inbox.role)}
              </span>
            </>
          )}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMapOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-zinc-700/80 bg-zinc-800/80 px-2.5 py-1.5 text-[12px] font-semibold text-zinc-200 hover:bg-zinc-700/80"
          >
            <Users className="h-3.5 w-3.5" /> Decision owners
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-zinc-700/80 bg-zinc-800/80 px-2.5 py-1.5 text-[12px] font-semibold text-zinc-200 hover:bg-zinc-700/80"
          >
            <Settings className="h-3.5 w-3.5" /> Settings
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {!error && total === 0 ? (
          <EmptyState icon={Mail}>
            No tracked mail threads on this project yet. Connect your mailbox in
            Profile Settings → Mail Intelligence.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-4">
            {SECTIONS.map(({ key, title }) =>
              inbox?.[key]?.length ? (
                <section key={key}>
                  <SectionLabel count={inbox[key].length}>{title}</SectionLabel>
                  <div className="flex flex-col gap-2">
                    {inbox[key].map((row) => (
                      <ThreadRow
                        key={row.thread_id}
                        row={row}
                        selected={row.thread_id === selectedId}
                        onClick={() => openDetail(row.thread_id)}
                      />
                    ))}
                  </div>
                </section>
              ) : null,
            )}
          </div>
        )}
      </div>

      <DrawerDetail open={mapOpen} onClose={() => setMapOpen(false)}>
        <DecisionMapPanel
          accessToken={accessToken}
          onClose={() => setMapOpen(false)}
          onSaved={reload}
        />
      </DrawerDetail>

      <DrawerDetail open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <MailSettingsPanel
          accessToken={accessToken}
          onClose={() => setSettingsOpen(false)}
        />
      </DrawerDetail>

      <DrawerDetail open={!!selectedId} onClose={closeDetail}>
        {detailLoading ? (
          <Spinner />
        ) : detailError ? (
          <div className="p-6">
            <ErrorBanner>{detailError}</ErrorBanner>
          </div>
        ) : (
          <MailThreadPanel
            accessToken={accessToken}
            thread={detail}
            onClose={closeDetail}
          />
        )}
      </DrawerDetail>
    </div>
  );
};

export default MailTriageList;
